import { RightEnum, type Vendor, type VendorRule, VendorTypeEnum } from "@finway-group/shared/lib/models"
import { Button, Col, Form, Row } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { VendorSelect } from "~Components/forms/expenseForm/commonInputFields/VendorSelect"
import { NewVendorDetectedIcon } from "~Components/icons/NewVendorDetectedIcon"
import { VendorFormModal } from "~Components/modals/vendor.modal"
import { VendorRuleModal } from "~Components/modals/vendorRule.modal"
import { useModal } from "~Shared/context/modal.context"
import { useCostCenters2 } from "~Shared/hooks/costCenter2.hooks"
import { useExpenseAccounts } from "~Shared/hooks/expenseAccount.hooks"
import { fetchVendor } from "~Shared/queries/vendors/vendor.queries"
import { AuthzService } from "~Shared/services"

interface VendorSelectionProps {
    onVendorChange: (
        vendor: string,
        options?: {
            costCenter?: string
            costCenter2?: string
            expenseAccount?: string
        },
    ) => void
    selectedVendor?: Vendor
    ocrRan?: boolean
    newVendorSuggestion?: Partial<Vendor>
    showRules?: boolean
}

const MINIMUM_VENDOR_RULES = 4

/**
 * TODO This is current only used for purchase orders and delivery notes, but it doesn't depend on those and since it's very similar to
 *   @see {src/components/forms/expenseForm/commonInputFields/VendorSelectField.tsx},
 *   maybe these two can be merged into one component?
 */
export const VendorSelection = ({ onVendorChange, selectedVendor, ocrRan = false, newVendorSuggestion, showRules = true }: VendorSelectionProps) => {
    const costCenters2 = useCostCenters2()
    const expenseAccounts = useExpenseAccounts()

    const { t } = useTranslation()
    const { showModal } = useModal()

    const selectedCreditor = selectedVendor

    const getVendorRuleCount = () => {
        const rules = selectedVendor?.rule ?? {}
        const numberOfRules = Object.values(rules).filter((rule) => (Array.isArray(rule) ? rule.length > 0 : !!rule)).length
        // if rules is not the empty object, it includes the id field which is not a rule and is therefore not included in the rule count
        return numberOfRules === 0 ? numberOfRules : numberOfRules - 1
    }

    const onVendorCreation = (_flag: boolean, vendor: Vendor) => {
        if (vendor) {
            onVendorChange(vendor.id, {
                costCenter: vendor.rule?.costCenter,
                costCenter2: vendor.rule?.costCenter2,
                expenseAccount: vendor.rule?.expenseAccount,
            })
        }
    }

    const showVendorRuleModal = (vendor: Vendor) => {
        showModal(VendorRuleModal, true, {
            isShowing: true,
            vendor,
            onSubmit: (rules: Pick<VendorRule, "costCenter" | "costCenter2" | "expenseAccount">) => {
                onVendorChange(vendor.id, {
                    costCenter: rules.costCenter,
                    costCenter2: rules.costCenter2,
                    expenseAccount: rules.expenseAccount,
                })
            },
        })
    }

    const renderVendorLabel = () => {
        const canSeeVendorRules = showRules && AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__ALL__READ)

        return (
            <>
                {selectedCreditor && canSeeVendorRules ? (
                    <span className="cursor-pointer" onClick={() => showVendorRuleModal(selectedCreditor)}>
                        {`${t("input:request.vendor")} `}
                        <u className="rule-link">{`(${t("label:rules")} ${getVendorRuleCount()}/${
                            MINIMUM_VENDOR_RULES + Number(costCenters2.length > 0) + Number(expenseAccounts.length > 0)
                        })`}</u>
                    </span>
                ) : (
                    <span>{t("input:request.vendor")}</span>
                )}
            </>
        )
    }

    const showVendorModal = () => {
        showModal(VendorFormModal, true, {
            isShowing: true,
            isNew: true,
            prefilledData: newVendorSuggestion,
            onCancel: onVendorCreation,
            shouldRedirectOnRestoration: false,
            isDraggable: true,
            isOpenedByExpenseModal: true,
        })
    }

    return (
        <Row gutter={[16, 16]}>
            <Col span={22}>
                <VendorSelect
                    formProps={{
                        style: { marginBottom: "0px" },
                        name: ["vendor", "_id"],
                        rules: [{ required: true, message: t("validation:required") }],
                        label: renderVendorLabel(),
                        extra: showRules && <p className="input-help">{t("info:vendor_rule")}</p>,
                    }}
                    shouldShowOnlyVerifiedVendors={false}
                    shouldRenderSelectDropdown={false}
                    selectedVendorType={VendorTypeEnum.VENDOR}
                    onSelect={async (creditorId) => {
                        const newVendor = await fetchVendor(creditorId)

                        onVendorChange(creditorId, {
                            costCenter: newVendor?.rule?.costCenter,
                            costCenter2: newVendor?.rule?.costCenter2,
                            expenseAccount: newVendor?.rule?.expenseAccount,
                        })
                    }}
                    selectedCreditorId={selectedVendor?.id}
                />
            </Col>

            <Col span={2} className="mb-8 h-full">
                {/* need empty label for proper alignment */}
                <Form.Item label=" ">
                    <Button icon={<NewVendorDetectedIcon isHighlighted={!selectedVendor && ocrRan} />} className="btn-default" onClick={showVendorModal} />
                </Form.Item>
            </Col>
        </Row>
    )
}
